import { type CSSProperties, Fragment, memo, useState } from 'react'

import { Link as RouterLink, useLocation } from 'react-router'

import {
  BorderRadius,
  Box,
  LightModeSemanticColors,
  Spacing,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { SideMenuDot } from '~/components/layouts/MainLayout/SideMenuDot'
import { useSideMenuDots } from '~/components/layouts/MainLayout/SideMenuDotsContext'
import { isMenuPathActive } from '~/components/layouts/MainLayout/menuGroups'
import { sideMenuRowPulseStyle } from '~/components/layouts/MainLayout/sideMenuDotPulse'
import type { SideMenuDot as SideMenuDotModel } from '~/models/sideMenuDot'

export type SubMenuItem =
  | { kind?: 'link'; path: string; label: string }
  | { kind: 'section'; label: string }
  | { kind: 'divider' }

type SubMenuItemRowProps = {
  path: string
  label: string
  isActive: boolean
  dot?: SideMenuDotModel
}

const SubMenuItemRow = memo(
  ({ path, label, isActive, dot }: SubMenuItemRowProps): React.ReactElement => {
    const [isHover, setIsHover] = useState(false)

    let backgroundColor = 'transparent'
    if (isActive) {
      backgroundColor = LightModeSemanticColors.Surface.infoLow.value
    } else if (isHover) {
      backgroundColor = LightModeSemanticColors.Hover.lowEmphasis.value
    }

    const style: CSSProperties = {
      padding: Spacing[2].value,
      borderRadius: BorderRadius.radiiMd.value,
      cursor: 'pointer',
      fontSize: fontSizes.md.value,
      minHeight: '37px',
      display: 'flex',
      alignItems: 'center',
      lineHeight: 1.4,
      transition: 'background-color 0.15s',
      backgroundColor,
      color: isActive
        ? LightModeSemanticColors.Text.accentPrimary.value
        : LightModeSemanticColors.Text.lowEmphasis.value,
      textDecoration: 'none',
      // design (.nav-flyout-item / .sidebar-secondary-item) は非選択 300 / 選択 600。
      // [DS差異] DS `SideMenuLayoutPopOverMenuItem` は textMd の normal (400) だが、
      // design を UI の正本とする (`design/CLAUDE.md`) ため design 値に合わせる。
      fontWeight: isActive ? 600 : 300,
    }

    const link = (
      <RouterLink
        aria-current={isActive ? 'page' : undefined}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        role="menuitem"
        style={style}
        to={path}
      >
        {label}
        {dot !== undefined && (
          // ドットはクリック導線を持たない。行の遷移はそのまま (右端に寄せる)。
          <Box as="span" marginLeft="auto" paddingLeft="2">
            <SideMenuDot dot={dot} rowPulses />
          </Box>
        )}
      </RouterLink>
    )
    if (dot === undefined) return link
    return (
      <Box asChild css={sideMenuRowPulseStyle(dot.severity)}>
        {link}
      </Box>
    )
  },
)

SubMenuItemRow.displayName = 'SubMenuItemRow'

const Separator = memo((): React.ReactElement => {
  const style: CSSProperties = {
    margin: `${Spacing[2].value} 0`,
    border: 'none',
    borderTop: `1px solid ${LightModeSemanticColors.Border.midEmphasis.value}`,
  }
  return <hr style={style} />
})
Separator.displayName = 'Separator'

// group 内の小分類ラベル (design の .nav-flyout-section / .sidebar-secondary-section)。
const SectionLabel = memo(
  ({ label }: { label: string }): React.ReactElement => {
    const style: CSSProperties = {
      // design: padding 6px 8px 2px。 上下は項目 (8px) より詰めて見出しらしく見せる
      padding: `6px ${Spacing[2].value} 2px`,
      // design 値。 DS の fontSizes スケール外 (xs=10px / sm=12px) だが design が SoT
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.04em',
      color: LightModeSemanticColors.Text.lowEmphasis.value,
      userSelect: 'none',
    }
    return <div style={style}>{label}</div>
  },
)
SectionLabel.displayName = 'SectionLabel'

export const SubMenuItems = ({
  items,
}: {
  items: SubMenuItem[]
}): React.ReactElement => {
  const location = useLocation()
  const dots = useSideMenuDots()

  return (
    <>
      {items.map((item, index) => {
        // section は区切り線 + 小分類ラベルで描画する (= design include-nav.js が
        // popup / 副 panel の双方で `sidebar-secondary-divider--inline` の後ろに
        // `.nav-flyout-section` / `.sidebar-secondary-section` のラベルを出す形)。
        // 先頭の section だけは線が上端と二重になるのでラベルのみ。
        if (item.kind === 'section') {
          return (
            <Fragment key={`section-${index}`}>
              {index > 0 && <Separator />}
              <SectionLabel label={item.label} />
            </Fragment>
          )
        }
        if (item.kind === 'divider') {
          return <Separator key={`divider-${index}`} />
        }
        return (
          <SubMenuItemRow
            key={item.path}
            dot={dots.get(item.path)}
            isActive={isMenuPathActive(location.pathname, item.path)}
            label={item.label}
            path={item.path}
          />
        )
      })}
    </>
  )
}
